import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../support/store';
import {
  removeJourneyMapDraftItem,
  updateJourneyMapDraftItem,
} from '../../../../support/slices/journeyMapBuilderSlice';
import { JourneyMapDraftItem } from '../../../../types/journeyMap';
import JourneyMapCompletionConfig from '../JourneyMapCompletionConfig';
import { normalizeJourneyMapCompletionConfig } from '../../../../support/journeyMapCompletion';
import {
  getAllowedContentRefTypeOptions,
  getContentOptionLabel,
  getContentRefTypeOption,
  getDefaultContentRefTypeForProvider,
  isMoodleCourseContentRefType,
  JOURNEY_MAP_PROVIDER_OPTIONS,
} from '../../../../support/journeyMap/contentSources';
import { normalizeProviderKey } from '../../../../support/journeyMap/providers';
import { MindtickleSeries } from '../../../../support/journeyMap/mindtickle';
import { JourneyMapFileRecord } from '../../../../support/journeyMap/files';
import JourneyMapFileDropper from './JourneyMapFileDropper';
import { ResourceFileRecord } from '../../../../support/resourceFiles';
import { DEFAULT_VIDEO_COMPLETION_THRESHOLD_PCT as VIDEO_DEFAULT_COMPLETION_THRESHOLD_PCT } from '../../../../support/journeyMap/videoProgress';
import ResourceFileDropper from './ResourceFileDropper';
import ResourceFileViewer from '../../../Enablement/JourneyMap/ResourceFileViewer';
import JourneyMapMindtickleSeriesPicker from './JourneyMapMindtickleSeriesPicker';
import { loadDcloudCatalogItems } from './journeyMapSidePanelUtils';
import { useAuthenticateUser } from '../../../../hooks/useAuthenticateUser';
import { useMoodleCourses } from '../../../../hooks/useMoodleHooks';
import { selectMoodleCategoriesMap } from '../../../../support/slices/moodleSlice';
import { RootState } from '../../../../support/store';
import { fetchItems } from '../../../../support/queries';

type Props = {
  item: JourneyMapDraftItem;
  providerItems: any[];
  providerItemsLoading: boolean;
};

type MoodleCourse = {
  id: number;
  fullname?: string;
  shortname?: string;
  summary?: string;
  categoryid?: number | string;
  category_id?: number | string;
  category?: number | string;
};

type MoodleCategory = {
  id: number | string;
  name: string;
};

type SnackableCourseRow = {
  id: number | string; // canonical row id persisted in content_ref_id
  deployed_moodle_id?: number | string | null;
  staged_moodle_id?: number | string | null;
  moodle_course_id?: number | string | null;
  course_id?: number | string | null;
  source?: string | null;
  instance?: string | null;
  mode?: string | null;
  title?: string;
  name?: string;
  shortname?: string;
  summary?: string;
};

/**
 * One selectable course in the Moodle / Cyber Academy picker.
 *
 * `id` is a composite `{contentRefType}:{contentRefId}` key, not the persisted reference. The
 * list mixes two sources whose id spaces overlap — ai_snackable_course primary keys and raw
 * Moodle course ids — so a bare numeric id cannot identify an option. `contentRefType` and
 * `contentRefId` are what actually get written to the item.
 */
type CourseOption = {
  id: string;
  contentRefType: 'ai_snackable_course' | 'moodle_course';
  contentRefId: string;
  deployed_moodle_id: number | null;
  title: string;
  name: string;
  shortname: string;
  summary: string;
  source: string;
  __row?: SnackableCourseRow | null;
  __course?: MoodleCourse | null;
};

const courseOptionKey = (contentRefType: string, contentRefId: string | number) =>
  `${contentRefType}:${contentRefId}`;

const DCLOUD_LAB_TYPE_OPTIONS = [
  { value: 'instant_demo', label: 'Instant Demo' },
  { value: 'scheduled_lab', label: 'Scheduled Lab' },
] as const;

const DCLOUD_DATA_CENTER_OPTIONS = [
  { value: 'sjc', label: 'San Jose' },
  { value: 'rtp', label: 'RTP' },
] as const;

const DCLOUD_SEARCH_TYPE_OPTIONS = [
  { value: 'solution', label: 'Solution' },
  { value: 'text', label: 'Text Search' },
] as const;

function asArray(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  return [];
}

function toNum(value: any): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// IMPORTANT RULE: staged_moodle_id fallback when deployed_moodle_id missing
function resolveSnackableMoodleId(row: any): number | null {
  const candidates = [
    row?.deployed_moodle_id,
    row?.staged_moodle_id,
    row?.moodle_course_id,
    row?.course_id,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function courseCategoryId(course: MoodleCourse): string {
  return String(course.categoryid ?? course.category_id ?? course.category ?? '');
}

function stripHtml(html?: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function matchesTrainTheTrainerTitle(course: MoodleCourse): boolean {
  const fullname = String(course?.fullname ?? '').trim().toLowerCase();
  const shortname = String(course?.shortname ?? '').trim().toLowerCase();

  const matchesTrainer =
    fullname.includes('train the trainer') ||
    fullname.includes('train the trainer-old') ||
    shortname.includes('ttt') ||
    fullname.includes(' ttt') ||
    fullname.startsWith('ttt ') ||
    fullname.endsWith(' ttt');

  const isDeprecated =
    fullname.includes('(deprecated)') || fullname.includes('-old') || shortname.includes('-old');

  return matchesTrainer && !isDeprecated;
}

const JourneyMapSidePanelItemEditor: React.FC<Props> = ({
                                                          item,
                                                          providerItems,
                                                          providerItemsLoading,
                                                        }) => {
  const dispatch = useAppDispatch();
  const { email } = useAuthenticateUser();

  const activeDraft = useSelector((state: RootState) => {
    const activeDraftId = state.journeyMapBuilder.activeDraftId;
    return state.journeyMapBuilder.drafts.find((d) => d.tempId === activeDraftId) || null;
  });

  const currentDraftJourneyMapId = activeDraft?.id != null ? String(activeDraft.id) : null;

  const [dcloudItems, setDcloudItems] = useState<any[]>([]);
  const [dcloudItemsLoading, setDcloudItemsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [snackableRows, setSnackableRows] = useState<SnackableCourseRow[]>([]);
  const [snackableRowsLoading, setSnackableRowsLoading] = useState(false);

  const providerValue = normalizeProviderKey(item.provider || '');
  const contentRefTypeValue =
    item.content_ref_type || getDefaultContentRefTypeForProvider(providerValue);

  const selectedContentRefType = useMemo(() => {
    return getContentRefTypeOption(contentRefTypeValue);
  }, [contentRefTypeValue]);

  const allowedContentRefTypes = useMemo(() => {
    return getAllowedContentRefTypeOptions(providerValue);
  }, [providerValue]);

  const selectedCompletionConfig = useMemo(() => {
    return normalizeJourneyMapCompletionConfig(item?.provider_config?.completion_tracking);
  }, [item]);

  const isDcloudProvider = providerValue === 'dcloud';
  const isCyberProvider = providerValue === 'cyber_academy' || providerValue.includes('cyber');
  const isMoodleProvider = providerValue === 'moodle';
  const isMindtickleProvider = providerValue === 'mindtickle';
  const isJourneyMapReferenceType = contentRefTypeValue === 'journey_map';
  const isMindtickleSeriesType = contentRefTypeValue === 'mindtickle_series';

  // The two providers that pick from a Moodle catalog, and the two ref types such a pick can
  // produce. Once an author selects a course with no snackable row the item's ref type becomes
  // `moodle_course`, so every branch below has to accept either type or the picker empties
  // itself the moment a selection is made.
  const isCourseCatalogProvider = isMoodleProvider || isCyberProvider;
  const isCourseRefType = isMoodleCourseContentRefType(contentRefTypeValue);
  const isCourseCatalogPicker = isCourseCatalogProvider && isCourseRefType;

  const dcloudConfig = item?.provider_config?.dcloud || {};
  const dcloudLabType = String(dcloudConfig.lab_type || 'instant_demo');
  const dcloudDataCenter = String(dcloudConfig.preferred_data_center || 'rtp');
  const dcloudSearchType = String(dcloudConfig.search_type || 'text');
  const dcloudSearchValue = String(dcloudConfig.search_value || '');

  // Keep existing hooks for enrichment / category filtering
  const { data: externalMoodleCourses = [], isLoading: externalMoodleCoursesLoading } =
    useMoodleCourses(
      {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
      'external'
    );

  const { data: internalMoodleCourses = [], isLoading: internalMoodleCoursesLoading } =
    useMoodleCourses(
      {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
      'internal'
    );

  const categoriesMap = useSelector(selectMoodleCategoriesMap);
  const allCategories = useMemo(
    () => Object.values(categoriesMap || {}) as MoodleCategory[],
    [categoriesMap]
  );

  const trainerCategoryIds = useMemo(() => {
    return allCategories
      .filter((cat) => String(cat?.name ?? '').trim().toLowerCase() === 'train the trainer')
      .map((cat) => String(cat.id));
  }, [allCategories]);

  const moodleTrainTheTrainerCourses = useMemo(() => {
    const all = Array.isArray(externalMoodleCourses) ? (externalMoodleCourses as MoodleCourse[]) : [];

    const filtered = trainerCategoryIds.length
      ? all.filter((course) => trainerCategoryIds.includes(courseCategoryId(course)))
      : all.filter(matchesTrainTheTrainerTitle);

    return [...filtered].sort((a, b) =>
      String(a.fullname ?? a.shortname ?? '').localeCompare(String(b.fullname ?? b.shortname ?? ''))
    );
  }, [externalMoodleCourses, trainerCategoryIds]);

  const cyberAcademyCourses = useMemo(() => {
    const all = Array.isArray(internalMoodleCourses) ? (internalMoodleCourses as MoodleCourse[]) : [];
    return [...all].sort((a, b) =>
      String(a.fullname ?? a.shortname ?? '').localeCompare(String(b.fullname ?? b.shortname ?? ''))
    );
  }, [internalMoodleCourses]);

  // Load canonical ai_snackable_course rows
  useEffect(() => {
    let active = true;

    const run = async () => {
      if (!isCourseRefType) {
        if (active) {
          setSnackableRows([]);
          setSnackableRowsLoading(false);
        }
        return;
      }

      if (active) setSnackableRowsLoading(true);
      try {
        const rows = await fetchItems(
          'db/ai-snackable-course/',
          { page: 1, size: 1000 },
          { includePageSize: false }
        );
        if (active) setSnackableRows(asArray(rows) as SnackableCourseRow[]);
      } catch {
        if (active) setSnackableRows([]);
      } finally {
        if (active) setSnackableRowsLoading(false);
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [isCourseRefType]);

  useEffect(() => {
    let active = true;

    const run = async () => {
      if (!isDcloudProvider) {
        if (active) {
          setDcloudItems([]);
          setDcloudItemsLoading(false);
        }
        return;
      }

      const query = String(dcloudSearchValue || '').trim();
      if (!query || !email) {
        if (active) {
          setDcloudItems([]);
          setDcloudItemsLoading(false);
        }
        return;
      }

      if (active) setDcloudItemsLoading(true);

      try {
        const items = await loadDcloudCatalogItems({
          email,
          text: query,
          preferred_data_center: dcloudDataCenter,
          page: 1,
          size: 1000,
          searchType: dcloudSearchType,
          labType: dcloudLabType,
        });

        if (active) setDcloudItems(items);
      } catch {
        if (active) setDcloudItems([]);
      } finally {
        if (active) setDcloudItemsLoading(false);
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [isDcloudProvider, dcloudSearchValue, dcloudDataCenter, dcloudSearchType, dcloudLabType, email]);

  // FORCE referenced journey-map completion mode
  useEffect(() => {
    if (!isJourneyMapReferenceType) return;

    const currentTracking = normalizeJourneyMapCompletionConfig(
      item?.provider_config?.completion_tracking
    );

    const needsUpdate =
      currentTracking.mode !== 'auto_complete' ||
      currentTracking.autoCompleteType !== 'course_completion';

    if (!needsUpdate) return;

    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          provider_config: {
            ...(item.provider_config || {}),
            completion_tracking: {
              ...currentTracking,
              mode: 'auto_complete',
              autoCompleteType: 'course_completion',
              thresholdPercent: null,
            },
            referenced_journey_map: {
              ...(item.provider_config as any)?.referenced_journey_map,
              source: 'referenced_journey_map_assignment',
            },
          },
        },
      })
    );
  }, [dispatch, isJourneyMapReferenceType, item]);

  // FORCE Mindtickle completion mode.
  //
  // Mindtickle completions only reach us through the nightly Snowflake sync, so the learner
  // cannot self-attest and the app cannot observe the finish. The item must stay on
  // external_sync for the launch endpoint to accept it, and pending_validation is the generic
  // opt-in the backend reads to decide that a launch is worth recording.
  useEffect(() => {
    // Keyed on the provider as well as the ref type so the item is pinned as soon as the
    // author picks Mindtickle, not only once they have chosen a series.
    if (!isMindtickleSeriesType && !isMindtickleProvider) return;

    const currentTracking = normalizeJourneyMapCompletionConfig(
      item?.provider_config?.completion_tracking
    );

    const pendingConfig = (item.provider_config as any)?.pending_validation || {};

    const needsUpdate =
      item.completion_mode !== 'external_sync' ||
      currentTracking.mode !== 'auto_complete' ||
      currentTracking.autoCompleteType !== 'course_completion' ||
      pendingConfig.enabled !== true;

    if (!needsUpdate) return;

    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          completion_mode: 'external_sync',
          provider_config: {
            ...(item.provider_config || {}),
            completion_tracking: {
              ...currentTracking,
              mode: 'auto_complete',
              autoCompleteType: 'course_completion',
              thresholdPercent: null,
            },
            pending_validation: {
              ...pendingConfig,
              enabled: true,
              source: 'mindtickle_nightly_sync',
            },
          },
        },
      })
    );
  }, [dispatch, isMindtickleProvider, isMindtickleSeriesType, item]);

  const handleSelectMindtickleSeries = (series: MindtickleSeries) => {
    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          content_ref_type: 'mindtickle_series',
          content_ref_id: String(series.series_id),
          launch_url: series.launch_url || '',
          provider_config: {
            ...(item.provider_config || {}),
            mindtickle: {
              ...((item.provider_config as any)?.mindtickle || {}),
              series_id: String(series.series_id),
              series_name: series.series_name || '',
              entry_module_id: series.entry_module_id || null,
            },
            // The generic key the sync reads to match a warehouse row back to this item.
            external_course_id: String(series.series_id),
          },
          metadata: {
            ...(item.metadata || {}),
            resolvedReferencedTitle: series.series_name || '',
          },
        },
      })
    );
  };

  const handleFileChange = (record: JourneyMapFileRecord | null) => {
    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          content_ref_type: 'file',
          content_ref_id: record ? String(record.id) : '',
          provider_config: {
            ...(item.provider_config || {}),
            // Cached so the learner modal can label the download without a second lookup.
            file: record
              ? {
                file_id: record.id,
                file_name: record.file_name,
                content_type: record.content_type,
                size_bytes: record.size_bytes,
                version: record.version,
                is_previewable: record.is_previewable === true,
              }
              : null,
          },
        },
      })
    );
  };

  /**
   * Attach (or clear) a file-backed `resource` for a `resource_file` item.
   *
   * The distinct ref type is load-bearing, not cosmetic: `content_ref_id` here is a
   * `resource.id`, while for `file` above it is a `journey_map_file.id`. Writing `'file'` for
   * both would leave the learner modal unable to tell which table to read, and it would not
   * error — it would resolve whichever row happened to share the number.
   */
  const handleResourceFileChange = (record: ResourceFileRecord | null) => {
    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          content_ref_type: 'resource_file',
          content_ref_id: record ? String(record.id) : '',
          provider_config: {
            ...(item.provider_config || {}),
            // Cached so the learner modal can label and route the file without a second
            // lookup. `render_kind` comes from the server rather than being re-derived in the
            // browser, so both ends agree on which renderer a content type gets.
            file: record
              ? {
                resource_id: record.id,
                file_name: record.file_name,
                content_type: record.content_type,
                size_bytes: record.size_bytes,
                version: record.version,
                render_kind: record.render_kind,
              }
              : null,
          },
          metadata: {
            ...(item.metadata || {}),
            resolvedReferencedTitle: record ? String(record.file_name || record.name || '') : '',
            // Seed a human-readable display label from the file name on first attach; never
            // overwrite one the admin has already customized.
            label: record
              ? item.metadata?.label || String(record.file_name || record.name || '')
              : item.metadata?.label || '',
          },
        },
      })
    );
  };

  /**
   * Attach (or clear) a file-backed `resource` for a `video` item.
   *
   * A video is a resource file whose content type is `video/*`; the only reason this is not
   * `handleResourceFileChange` is the ref type. `content_ref_type` is `'video'` (not
   * `'resource_file'`) so the learner modal routes it to the native `<video>` player and its
   * watched-coverage completion, and the cached `provider_config.file` shape is identical so the
   * player can read `resource_id`/`version` without a second lookup.
   */
  const handleVideoFileChange = (record: ResourceFileRecord | null) => {
    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          content_ref_type: 'video',
          content_ref_id: record ? String(record.id) : '',
          provider_config: {
            ...(item.provider_config || {}),
            file: record
              ? {
                resource_id: record.id,
                file_name: record.file_name,
                content_type: record.content_type,
                size_bytes: record.size_bytes,
                version: record.version,
                render_kind: record.render_kind,
              }
              : null,
          },
          metadata: {
            ...(item.metadata || {}),
            resolvedReferencedTitle: record ? String(record.file_name || record.name || '') : '',
            label: record
              ? item.metadata?.label || String(record.file_name || record.name || '')
              : item.metadata?.label || '',
          },
        },
      })
    );
  };

  /**
   * Percent of the video a learner must watch before the step auto-completes. Enforced
   * server-side against `provider_config.completion_threshold_pct`; the field only sets it.
   * Empty resets to the default (90) so the server never sees a threshold of 0.
   */
  const handleVideoThresholdChange = (raw: string) => {
    const trimmed = String(raw ?? '').trim();
    const next =
      trimmed === ''
        ? VIDEO_DEFAULT_COMPLETION_THRESHOLD_PCT
        : Math.min(100, Math.max(1, Math.round(Number(trimmed))));

    if (!Number.isFinite(next)) return;

    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          provider_config: {
            ...(item.provider_config || {}),
            completion_threshold_pct: next,
          },
        },
      })
    );
  };

  /**
   * The selectable course list: every snackable row (no over-filtering) UNIONED with the
   * Moodle courses that no snackable row covers.
   *
   * The union is the point. Building the list from `snackableRows` alone silently dropped
   * every Cyber Academy course that had not been through snackable generation — the course
   * was live in Moodle and assignable everywhere else in the toolbox, but simply absent from
   * this picker. Those courses now appear as `moodle_course` references, which carry the
   * Moodle course id instead of a snackable row id.
   *
   * A Moodle course counts as covered only when a snackable row resolves to a course that is
   * actually present in *this instance's* catalog. Internal and external Moodle are separate
   * deployments with independent id spaces, so treating a bare id from an external row as
   * proof that internal course 42 is covered would hide a real course.
   */
  const courseOptions = useMemo<CourseOption[]>(() => {
    if (!isCourseRefType) return [];

    const moodleList = isCyberProvider ? cyberAcademyCourses : moodleTrainTheTrainerCourses;
    const fallbackSource = isCyberProvider
      ? 'cyber_academy_internal'
      : 'train_the_trainer_external';

    const moodleById = new Map<number, MoodleCourse>();
    moodleList.forEach((course) => {
      const courseId = toNum(course.id);
      if (courseId != null) moodleById.set(courseId, course);
    });

    const options: CourseOption[] = [];
    const coveredMoodleIds = new Set<number>();

    (Array.isArray(snackableRows) ? snackableRows : []).forEach((row) => {
      if (row?.id == null || String(row.id).trim() === '') return;

      const resolvedMoodleId = resolveSnackableMoodleId(row);
      const moodleMatch =
        resolvedMoodleId == null ? null : moodleById.get(resolvedMoodleId) || null;

      if (moodleMatch && resolvedMoodleId != null) coveredMoodleIds.add(resolvedMoodleId);

      const title = String(
        row?.title ||
        row?.name ||
        moodleMatch?.fullname ||
        moodleMatch?.shortname ||
        `Course Row ${row.id}`
      ).trim();

      options.push({
        id: courseOptionKey('ai_snackable_course', row.id),
        contentRefType: 'ai_snackable_course',
        contentRefId: String(row.id),
        deployed_moodle_id: resolvedMoodleId,
        title,
        name: title,
        shortname: String(row?.shortname || moodleMatch?.shortname || ''),
        summary: stripHtml(String(row?.summary || moodleMatch?.summary || '')),
        source: String(row?.source || fallbackSource),
        __row: row,
        __course: moodleMatch,
      });
    });

    moodleList.forEach((course) => {
      const courseId = toNum(course.id);
      if (courseId == null || coveredMoodleIds.has(courseId)) return;

      const title = String(course.fullname || course.shortname || `Course ${courseId}`).trim();

      options.push({
        id: courseOptionKey('moodle_course', courseId),
        contentRefType: 'moodle_course',
        contentRefId: String(courseId),
        deployed_moodle_id: courseId,
        title,
        name: title,
        shortname: String(course.shortname || ''),
        summary: stripHtml(String(course.summary || '')),
        source: fallbackSource,
        __row: null,
        __course: course,
      });
    });

    // De-dup on the composite key so a repeated snackable row cannot double up, while an
    // ai_snackable_course row and a moodle_course that share a number stay distinct.
    const dedup = new Map<string, CourseOption>();
    options.forEach((option) => {
      if (!dedup.has(option.id)) dedup.set(option.id, option);
    });

    return Array.from(dedup.values()).sort((a, b) =>
      String(a.title || '').localeCompare(String(b.title || ''))
    );
  }, [
    isCourseRefType,
    isCyberProvider,
    snackableRows,
    cyberAcademyCourses,
    moodleTrainTheTrainerCourses,
  ]);

  const effectiveProviderItems = useMemo(() => {
    if (isCourseCatalogPicker) {
      return courseOptions;
    }

    if (contentRefTypeValue === 'journey_map') {
      const base = Array.isArray(providerItems) ? providerItems : [];
      if (!currentDraftJourneyMapId) return base;

      return base.filter(
        (row: any) => String(row?.id ?? '') !== String(currentDraftJourneyMapId)
      );
    }

    return providerItems;
  }, [
    contentRefTypeValue,
    isCourseCatalogPicker,
    courseOptions,
    providerItems,
    currentDraftJourneyMapId,
  ]);

  const effectiveProviderItemsLoading = useMemo(() => {
    // Both datasets gate the list now: the Moodle catalog is no longer only a label
    // enrichment, it is a source of options, so showing the picker before it lands would look
    // like the unbacked courses are missing again.
    if (isMoodleProvider && isCourseRefType) {
      return snackableRowsLoading || externalMoodleCoursesLoading;
    }
    if (isCyberProvider && isCourseRefType) {
      return snackableRowsLoading || internalMoodleCoursesLoading;
    }
    return providerItemsLoading;
  }, [
    isMoodleProvider,
    isCyberProvider,
    isCourseRefType,
    snackableRowsLoading,
    externalMoodleCoursesLoading,
    internalMoodleCoursesLoading,
    providerItemsLoading,
  ]);

  const selectedReferencedItemLabel = useMemo(() => {
    if (isDcloudProvider) {
      if (!item?.content_ref_id) return '';
      const match = dcloudItems.find(
        (providerItem) =>
          String(providerItem.contentId ?? providerItem.uid ?? providerItem.id) ===
          String(item.content_ref_id),
      );
      if (!match) return String(item.content_ref_id);
      return getContentOptionLabel(match, ['name', 'title', 'uid', 'contentId', 'id']);
    }

    if (!item?.content_ref_id) return '';

    const optionId = isCourseCatalogPicker
      ? courseOptionKey(contentRefTypeValue, item.content_ref_id)
      : String(item.content_ref_id);

    const match = effectiveProviderItems.find(
      (providerItem) => String(providerItem.id) === optionId,
    );
    if (!match) return String(item.content_ref_id);

    return (
      String((item.metadata as any)?.resolvedReferencedTitle || '').trim() ||
      getContentOptionLabel(match, selectedContentRefType?.displayFields)
    );
  }, [
    isDcloudProvider,
    dcloudItems,
    effectiveProviderItems,
    isCourseCatalogPicker,
    contentRefTypeValue,
    item?.content_ref_id,
    item?.metadata,
    selectedContentRefType,
  ]);

  // `moodle_course` has no endpoint — there is no toolbox_db model to resolve it against — so
  // the course picker has to be allowed through on the provider instead of on the ref type,
  // or an unbacked course selection would drop the author back to the raw-id text field.
  //
  // `resource_file` is the mirror case: it *does* have an endpoint (`db/resource/` resolves its
  // id, which is what the canvas uses for labels), but it must not fall to the generic record
  // select — that would list every resource, files and non-files alike, instead of the dropper.
  const usesSelectableReferencedContent =
    isCourseCatalogPicker ||
    (!!selectedContentRefType?.endpoint &&
      !['markdown', 'file', 'resource_file', 'link', 'dcloud_catalog'].includes(
        contentRefTypeValue,
      ));

  const selectedDcloudItem = useMemo(() => {
    if (!isDcloudProvider || !item?.content_ref_id) return null;
    return (
      dcloudItems.find(
        (entry) => String(entry.contentId ?? entry.uid ?? entry.id) === String(item.content_ref_id),
      ) || null
    );
  }, [isDcloudProvider, dcloudItems, item?.content_ref_id]);

  const updateDcloudConfig = (changes: Record<string, any>) => {
    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: {
          provider_config: {
            ...(item.provider_config || {}),
            dcloud: {
              ...(item.provider_config?.dcloud || {}),
              ...changes,
            },
          },
        },
      }),
    );
  };

  // prevent MUI out-of-range warning when options are temporarily empty/not loaded yet
  const referencedSelectValue = useMemo(() => {
    const current = String(item.content_ref_id || '');
    if (!current) return '';

    const candidate = isCourseCatalogPicker
      ? courseOptionKey(contentRefTypeValue, current)
      : current;

    const exists = (effectiveProviderItems || []).some(
      (x: any) => String(x?.id) === candidate
    );
    return exists ? candidate : '';
  }, [item.content_ref_id, contentRefTypeValue, isCourseCatalogPicker, effectiveProviderItems]);

  // A file-backed item caches its file under `provider_config.file` at attach time; that is what
  // both the editable label and the in-panel preview read.
  const attachedFile = (item.provider_config as any)?.file || null;

  const handleLabelChange = (value: string) =>
    dispatch(
      updateJourneyMapDraftItem({
        tempId: item.tempId,
        changes: { metadata: { ...(item.metadata || {}), label: value } },
      }),
    );

  // Editable display label (defaults to the file name) + a Preview button that opens the shared
  // ResourceFileViewer. Rendered inside both the resource_file and video branches.
  const renderFileLabelAndPreview = () => {
    if (!attachedFile) return null;
    return (
      <>
        <TextField
          label="Display label"
          size="small"
          value={item.metadata?.label ?? ''}
          placeholder={String(attachedFile.file_name || '')}
          onChange={(e) => handleLabelChange(e.target.value)}
          helperText="Shown on the item card. Defaults to the file name; edit for something more readable."
          fullWidth
        />
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {item.item_type === 'section' ? 'Section' : 'Item'}
        </Typography>

        <TextField
          label="Name"
          size="small"
          value={item.name}
          onChange={(e) =>
            dispatch(
              updateJourneyMapDraftItem({
                tempId: item.tempId,
                changes: { name: e.target.value },
              }),
            )
          }
          fullWidth
        />

        <TextField
          label="Description"
          size="small"
          value={item.description || ''}
          onChange={(e) =>
            dispatch(
              updateJourneyMapDraftItem({
                tempId: item.tempId,
                changes: { description: e.target.value },
              }),
            )
          }
          multiline
          minRows={3}
          fullWidth
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="item-type-label">Item Type</InputLabel>
          <Select
            labelId="item-type-label"
            label="Item Type"
            value={item.item_type || 'content'}
            onChange={(e) =>
              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: { item_type: e.target.value as any },
                }),
              )
            }
          >
            <MenuItem value="content">Content</MenuItem>
            <MenuItem value="section">Section</MenuItem>
            <MenuItem value="assessment">Assessment</MenuItem>
            <MenuItem value="task">Task</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel id="provider-label">Provider</InputLabel>
          <Select
            labelId="provider-label"
            label="Provider"
            value={providerValue}
            onChange={(e) => {
              const nextProvider = String(e.target.value);
              const nextContentRefType = getDefaultContentRefTypeForProvider(nextProvider);

              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: {
                    provider: nextProvider,
                    content_ref_type: nextContentRefType,
                    content_ref_id: '',
                    launch_url: nextProvider === 'link' ? item.launch_url || '' : '',
                    provider_config:
                      nextProvider === 'dcloud'
                        ? {
                          ...(item.provider_config || {}),
                          dcloud: {
                            lab_type: 'instant_demo',
                            preferred_data_center: 'rtp',
                            search_type: 'text',
                            search_value: '',
                          },
                        }
                        : nextProvider === 'moodle'
                          ? {
                            ...(item.provider_config || {}),
                            instance: 'external',
                            mode: 'external',
                          }
                          : nextProvider === 'cyber_academy'
                            ? {
                              ...(item.provider_config || {}),
                              instance: 'internal',
                              mode: 'internal',
                            }
                            : nextProvider === 'mindtickle'
                              ? {
                                ...(item.provider_config || {}),
                                mindtickle: {},
                                // Opt this item into launch tracking: Mindtickle completions
                                // arrive from the nightly sync, so a launch has to be
                                // recorded or the step looks untouched until tomorrow.
                                pending_validation: {
                                  enabled: true,
                                  source: 'mindtickle_nightly_sync',
                                },
                              }
                              : nextProvider === 'video'
                                ? {
                                  ...(item.provider_config || {}),
                                  // Seed the default so the field is never blank and the
                                  // server sees a sane threshold before the admin touches it.
                                  completion_threshold_pct:
                                    item.provider_config?.completion_threshold_pct ??
                                    VIDEO_DEFAULT_COMPLETION_THRESHOLD_PCT,
                                }
                                : item.provider_config || {},
                  },
                }),
              );
            }}
          >
            {JOURNEY_MAP_PROVIDER_OPTIONS.map((provider) => (
              <MenuItem key={provider.value} value={provider.value}>
                {provider.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {allowedContentRefTypes.length > 1 ? (
          <FormControl size="small" fullWidth>
            <InputLabel id="content-ref-type-label">Content Type</InputLabel>
            <Select
              labelId="content-ref-type-label"
              label="Content Type"
              value={contentRefTypeValue}
              onChange={(e) =>
                dispatch(
                  updateJourneyMapDraftItem({
                    tempId: item.tempId,
                    changes: {
                      content_ref_type: String(e.target.value),
                      content_ref_id: '',
                    },
                  }),
                )
              }
            >
              {allowedContentRefTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        {contentRefTypeValue === 'markdown' ? (
          <TextField
            label="Markdown Content"
            size="small"
            value={String(item.content_ref_id || '')}
            onChange={(e) =>
              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: {
                    content_ref_type: 'markdown',
                    content_ref_id: e.target.value,
                  },
                }),
              )
            }
            fullWidth
            multiline
            minRows={8}
          />
        ) : contentRefTypeValue === 'resource_file' ? (
          <Stack spacing={2}>
            <ResourceFileDropper
              value={item.content_ref_id ?? null}
              onChange={handleResourceFileChange}
              libraryExcludeKind="video"
              onManualReferenceChange={(nextValue) =>
                dispatch(
                  updateJourneyMapDraftItem({
                    tempId: item.tempId,
                    changes: {
                      content_ref_type: 'resource_file',
                      content_ref_id: nextValue,
                    },
                  }),
                )
              }
            />
            {renderFileLabelAndPreview()}
          </Stack>
        ) : contentRefTypeValue === 'video' ? (
          <Stack spacing={2}>
            <ResourceFileDropper
              value={item.content_ref_id ?? null}
              onChange={handleVideoFileChange}
              accept="video/mp4,video/webm"
              libraryRenderKind="video"
              onManualReferenceChange={(nextValue) =>
                dispatch(
                  updateJourneyMapDraftItem({
                    tempId: item.tempId,
                    changes: {
                      content_ref_type: 'video',
                      content_ref_id: nextValue,
                    },
                  }),
                )
              }
            />
            <TextField
              label="Completion threshold (%)"
              type="number"
              size="small"
              value={
                item.provider_config?.completion_threshold_pct ??
                VIDEO_DEFAULT_COMPLETION_THRESHOLD_PCT
              }
              onChange={(e) => handleVideoThresholdChange(e.target.value)}
              inputProps={{ min: 1, max: 100, step: 1 }}
              helperText="Percent of the video a learner must watch for this step to auto-complete."
              sx={{ maxWidth: 240 }}
            />
            {renderFileLabelAndPreview()}
          </Stack>
        ) : contentRefTypeValue === 'file' ? (
          <JourneyMapFileDropper
            journeyMapId={currentDraftJourneyMapId}
            journeyMapItemId={item.id ?? null}
            value={item.content_ref_id ?? null}
            onChange={handleFileChange}
            onManualReferenceChange={(nextValue) =>
              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: {
                    content_ref_type: 'file',
                    content_ref_id: nextValue,
                  },
                }),
              )
            }
          />
        ) : isMindtickleSeriesType ? (
          <JourneyMapMindtickleSeriesPicker
            value={item.content_ref_id ?? null}
            storedSeriesName={String(
              (item.provider_config as any)?.mindtickle?.series_name || '',
            )}
            onSelect={handleSelectMindtickleSeries}
          />
        ) : contentRefTypeValue === 'link' ? (
          <TextField
            label="External URL"
            size="small"
            value={item.launch_url || ''}
            onChange={(e) =>
              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: {
                    content_ref_type: 'link',
                    launch_url: e.target.value,
                  },
                }),
              )
            }
            fullWidth
          />
        ) : isDcloudProvider ? (
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="dcloud-lab-type-label">Lab Type</InputLabel>
              <Select
                labelId="dcloud-lab-type-label"
                label="Lab Type"
                value={dcloudLabType}
                onChange={(e) => {
                  updateDcloudConfig({ lab_type: String(e.target.value) });
                  dispatch(
                    updateJourneyMapDraftItem({
                      tempId: item.tempId,
                      changes: { content_ref_id: '' },
                    }),
                  );
                }}
              >
                {DCLOUD_LAB_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="dcloud-data-center-label">Data Center</InputLabel>
              <Select
                labelId="dcloud-data-center-label"
                label="Data Center"
                value={dcloudDataCenter}
                onChange={(e) => {
                  updateDcloudConfig({ preferred_data_center: String(e.target.value) });
                  dispatch(
                    updateJourneyMapDraftItem({
                      tempId: item.tempId,
                      changes: { content_ref_id: '' },
                    }),
                  );
                }}
              >
                {DCLOUD_DATA_CENTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="dcloud-search-type-label">Search Type</InputLabel>
              <Select
                labelId="dcloud-search-type-label"
                label="Search Type"
                value={dcloudSearchType}
                onChange={(e) => {
                  updateDcloudConfig({ search_type: String(e.target.value) });
                  dispatch(
                    updateJourneyMapDraftItem({
                      tempId: item.tempId,
                      changes: { content_ref_id: '' },
                    }),
                  );
                }}
              >
                {DCLOUD_SEARCH_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={dcloudSearchType === 'solution' ? 'Solution Search' : 'Text Search'}
              size="small"
              value={dcloudSearchValue}
              onChange={(e) => {
                updateDcloudConfig({ search_value: e.target.value });
                dispatch(
                  updateJourneyMapDraftItem({
                    tempId: item.tempId,
                    changes: { content_ref_id: '' },
                  }),
                );
              }}
              helperText="Used to search the dCloud catalog for matching instant demos or scheduled labs."
              fullWidth
            />

            <FormControl size="small" fullWidth disabled={dcloudItemsLoading || !dcloudSearchValue.trim()}>
              <InputLabel id="dcloud-result-label">
                {dcloudItemsLoading ? 'Loading dCloud Content...' : 'dCloud Result'}
              </InputLabel>
              <Select
                labelId="dcloud-result-label"
                label={dcloudItemsLoading ? 'Loading dCloud Content...' : 'dCloud Result'}
                value={item.content_ref_id ? String(item.content_ref_id) : ''}
                onChange={(e) => {
                  const nextValue = String(e.target.value);
                  const selectedResult =
                    dcloudItems.find(
                      (entry) => String(entry.contentId ?? entry.uid ?? entry.id) === nextValue,
                    ) || null;

                  const selectedName = String(
                    selectedResult?.name || selectedResult?.title || selectedResult?.handle || ''
                  ).trim();

                  const selectedDescription = String(selectedResult?.description || '')
                    .replace(/<[^>]+>/g, '')
                    .trim();

                  dispatch(
                    updateJourneyMapDraftItem({
                      tempId: item.tempId,
                      changes: {
                        content_ref_type: 'dcloud_catalog',
                        content_ref_id: nextValue,
                        launch_url:
                          selectedResult?.links?.find((l: any) => l?.href)?.href ||
                          item.launch_url ||
                          '',
                        description: selectedName || selectedDescription || item.description || '',
                        metadata: {
                          ...(item.metadata || {}),
                          resolvedReferencedTitle: selectedName,
                          dcloud_selected_item: selectedResult
                            ? {
                              contentId: selectedResult.contentId ?? null,
                              uid: selectedResult.uid ?? null,
                              name: selectedName,
                              contentType: selectedResult.contentType || '',
                              guid: selectedResult.guid || '',
                            }
                            : null,
                        },
                      },
                    }),
                  );
                }}
              >
                {dcloudItems.map((entry) => {
                  const value = String(entry.contentId ?? entry.uid ?? entry.id);
                  const label = getContentOptionLabel(entry, ['name', 'title', 'uid', 'contentId', 'id']);
                  return (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {selectedDcloudItem ? (
              <Box sx={{ px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Selected dCloud Content
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedDcloudItem?.name || selectedDcloudItem?.title || selectedReferencedItemLabel}
                </Typography>
                {selectedDcloudItem?.contentType ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Type: {String(selectedDcloudItem.contentType)}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        ) : usesSelectableReferencedContent ? (
          <FormControl size="small" fullWidth disabled={effectiveProviderItemsLoading}>
            <InputLabel id="referenced-item-label">
              {effectiveProviderItemsLoading ? 'Loading Content...' : 'Referenced Item'}
            </InputLabel>
            <Select
              labelId="referenced-item-label"
              label={effectiveProviderItemsLoading ? 'Loading Content...' : 'Referenced Item'}
              value={referencedSelectValue}
              onChange={(e) => {
                const selectedId = String(e.target.value);

                if (isCourseCatalogPicker) {
                  const selectedCourse = effectiveProviderItems.find(
                    (course: any) => String(course.id) === selectedId
                  ) as CourseOption | undefined;

                  if (!selectedCourse) return;

                  // A snackable-backed pick still prefers the row's deployed id (falling back
                  // to staged); an unbacked pick only ever has the catalog's own course id.
                  const resolvedMoodleId =
                    resolveSnackableMoodleId(selectedCourse.__row) ??
                    toNum(selectedCourse.deployed_moodle_id) ??
                    null;

                  const resolvedTitle = String(
                    selectedCourse.title ||
                    selectedCourse.name ||
                    selectedCourse.__course?.fullname ||
                    selectedCourse.__course?.shortname ||
                    ''
                  ).trim();

                  const isSnackableBacked =
                    selectedCourse.contentRefType === 'ai_snackable_course';

                  dispatch(
                    updateJourneyMapDraftItem({
                      tempId: item.tempId,
                      changes: {
                        // Either the canonical snackable row id or, for a course with no
                        // snackable row, the Moodle course id. `content_ref_type` is what
                        // tells every reader which of the two this is.
                        content_ref_id: selectedCourse.contentRefId,
                        content_ref_type: selectedCourse.contentRefType,
                        provider_config: {
                          ...(item.provider_config || {}),
                          instance: isCyberProvider ? 'internal' : 'external',
                          mode: isCyberProvider ? 'internal' : 'external',
                          moodle: {
                            ...(item.provider_config as any)?.moodle,
                            // runtime course id should be deployed, else staged fallback
                            course_id: resolvedMoodleId,
                            selected_snackable_course_id: isSnackableBacked
                              ? selectedCourse.contentRefId
                              : null,
                            source: isCyberProvider
                              ? 'cyber_academy_internal'
                              : 'train_the_trainer_external',
                          },
                        },
                        metadata: {
                          ...(item.metadata || {}),
                          resolvedReferencedTitle: resolvedTitle,
                          selected_moodle_course_id: resolvedMoodleId,
                          selected_deployed_moodle_id: resolvedMoodleId,
                        },
                      },
                    }),
                  );
                  return;
                }

                dispatch(
                  updateJourneyMapDraftItem({
                    tempId: item.tempId,
                    changes: {
                      content_ref_id: selectedId,
                    },
                  }),
                );
              }}
            >
              {effectiveProviderItems.map((providerItem: any) => (
                <MenuItem key={providerItem.id} value={String(providerItem.id)}>
                  {getContentOptionLabel(providerItem, selectedContentRefType?.displayFields)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Referenced Item"
            size="small"
            value={selectedReferencedItemLabel || String(item.content_ref_id || '')}
            onChange={(e) =>
              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: {
                    content_ref_id: e.target.value,
                  },
                }),
              )
            }
            fullWidth
          />
        )}

        {contentRefTypeValue === 'journey_map' && currentDraftJourneyMapId ? (
          <Typography variant="caption" color="text.secondary">
            Note: this journey cannot reference itself and is excluded from the list.
          </Typography>
        ) : null}

        {isJourneyMapReferenceType ? (
          <Box
            sx={{
              p: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Completion Tracking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Referenced Journey Map items always auto-complete based on the learner’s
              completion status in the referenced journey map.
            </Typography>
          </Box>
        ) : isMindtickleSeriesType || isMindtickleProvider ? (
          <Box
            sx={{
              p: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Completion Tracking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mindtickle completions come from the nightly Snowflake sync, so this step cannot
              be completed manually. When a learner launches the series the step shows as
              awaiting confirmation until the sync reports the completion, or until the grace
              period lapses.
            </Typography>
            <TextField
              label="Grace period (days)"
              size="small"
              type="number"
              sx={{ mt: 1.5 }}
              value={String(
                (item.provider_config as any)?.pending_validation?.grace_days ?? '',
              )}
              onChange={(e) => {
                const raw = e.target.value.trim();
                const parsed = Number(raw);

                dispatch(
                  updateJourneyMapDraftItem({
                    tempId: item.tempId,
                    changes: {
                      provider_config: {
                        ...(item.provider_config || {}),
                        pending_validation: {
                          ...((item.provider_config as any)?.pending_validation || {}),
                          enabled: true,
                          grace_days:
                            raw && Number.isFinite(parsed) && parsed > 0
                              ? Math.floor(parsed)
                              : null,
                        },
                      },
                    },
                  }),
                );
              }}
              inputProps={{ min: 1 }}
              helperText="How long an unconfirmed launch stays pending before it is flagged. Leave blank for the default (2 days)."
              fullWidth
            />
          </Box>
        ) : (
          <JourneyMapCompletionConfig
            item={item}
            value={selectedCompletionConfig}
            onChange={(nextConfig) =>
              dispatch(
                updateJourneyMapDraftItem({
                  tempId: item.tempId,
                  changes: {
                    provider_config: {
                      ...(item.provider_config || {}),
                      completion_tracking: nextConfig,
                    },
                  },
                }),
              )
            }
          />
        )}

        <Button
          color="error"
          variant="contained"
          onClick={() => dispatch(removeJourneyMapDraftItem(item.tempId))}
        >
          Delete Item
        </Button>
      </Stack>

      {attachedFile ? (
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
          >
            <span>{item.metadata?.label || String(attachedFile.file_name || 'Preview')}</span>
            <IconButton aria-label="Close preview" onClick={() => setPreviewOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <ResourceFileViewer
              resourceId={attachedFile.resource_id ?? item.content_ref_id}
              fileName={attachedFile.file_name}
              contentType={attachedFile.content_type}
              sizeBytes={attachedFile.size_bytes}
              renderKind={attachedFile.render_kind}
              version={attachedFile.version}
              autoPreview
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </Box>
  );
};

export default JourneyMapSidePanelItemEditor;